/**
 * Mobile keypad for one stocktake line's count_expression.
 *
 * A small calculator: build an expression of summed terms, e.g.
 * `5 + 2×20 − 1 = 44`.
 *
 *   - The digit buffer is the CURRENT entry, always editable. Tapping
 *     digits appends (1 → 10), ⌫ backspaces (10 → 1). The buffer is NEVER
 *     cleared by the auto-submit, so the operator can keep editing the
 *     value they just typed.
 *   - The three mode buttons [🔢 loose] [📦 crates] [🚫 broken] each SEAL
 *     the current entry into the expression and start a NEW
 *     addition/subtraction of that kind. (Tapping a mode with an empty
 *     buffer just selects the kind for the next digits.) This is how extra
 *     values get chained — and why an explicit loose button exists: it is
 *     how you return to a `+` term after a crates/broken one. Loose-only
 *     articles (packSize null) disable [crates].
 *   - A single CALC LINE shows the sealed expression (normal weight), the
 *     in-flight entry (bold + larger), and the running total — there is no
 *     separate preview row; the lit mode button signals the current kind.
 *   - Auto-submit (1500ms debounce) ONLY sends the current expression to
 *     the server; it never clears or seals the buffer.
 *   - ↶ undo peels back: clears the in-flight entry, else re-opens the
 *     last sealed term for editing. C clears everything.
 *   - A pending (un-submitted) edit is flushed on unmount, so switching
 *     rows mid-entry never drops a count.
 *
 * The component is a small island within the StocktakeDetailPage mobile
 * bottom-bar. It does NOT replace the bare `Numpad` used when the operator
 * toggles the expression keypad off; both coexist. The parent keys this
 * component by the active chain-root, so each row gets a fresh buffer; on
 * mount the row's LAST committed term loads into the editable buffer and
 * the rest become the sealed expression.
 */

import { Button } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  render,
  stripSealed,
  sumSigned,
  type Term,
  type TermKind,
} from "../../utils/countExpression";

/** Debounce window for the server submit, in ms. Long enough to allow
 * multi-digit typing without firing mid-entry, short enough to feel
 * snappy. Matches the spec's locked 1500ms. */
const AUTOCOMMIT_MS = 1500;

/** Split a stored term list into (sealed expression, editable last entry).
 * The last term loads into the buffer so the operator can immediately edit
 * it; everything before it is the committed expression. Sealed legacy
 * flags are stripped — editing always produces a fresh, unsealed list. */
function splitTerms(terms: readonly Term[] | null): {
  committed: Term[];
  digits: string;
  mode: TermKind;
} {
  const t = stripSealed(terms ?? []);
  if (t.length === 0) return { committed: [], digits: "", mode: "loose" };
  const last = t[t.length - 1];
  return { committed: t.slice(0, -1), digits: String(last.n), mode: last.kind };
}

/** The term for the current buffer, or null when empty / non-positive
 * (or a crates entry with no pack size). */
function currentTermOf(
  digits: string,
  mode: TermKind,
  packSize: number | null,
): Term | null {
  const n = digits === "" ? 0 : Number.parseInt(digits, 10);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (mode === "crates") {
    if (packSize == null || packSize <= 0) return null;
    return { kind: "crates", n, pack_size: packSize };
  }
  if (mode === "broken") return { kind: "broken", n };
  return { kind: "loose", n };
}

interface Props {
  /** Current committed term list, or null when uncounted. */
  terms: Term[] | null;
  /** Article's snapshotted pack_size; null disables the crates button. */
  packSize: number | null;
  /** Disabled when the row is offline, reverted, or otherwise locked. */
  disabled?: boolean;
  /** Called after a submit / undo / clear. Parent persists via PATCH. */
  onCommit: (terms: Term[], countedQty: number) => void;
  /** Reports the LIVE running total (or null when empty) on every change,
   * so the info bar's Counted/Mismatch track what's being typed instead of
   * only the last server submit. Pass a stable setter to avoid effect loops. */
  onLiveTotal?: (total: number | null) => void;
}

export const CountExpressionKeypad = ({
  terms,
  packSize,
  disabled,
  onCommit,
  onLiveTotal,
}: Props) => {
  // Seeded ONCE per row (the parent remounts us via `key`). After mount we
  // own this state; server echoes from our own submits never clobber the
  // operator's in-flight edit.
  const [committed, setCommitted] = useState<Term[]>(
    () => splitTerms(terms).committed,
  );
  const [digits, setDigits] = useState<string>(() => splitTerms(terms).digits);
  const [mode, setMode] = useState<TermKind>(() => splitTerms(terms).mode);

  const current = useMemo(
    () => currentTermOf(digits, mode, packSize),
    [digits, mode, packSize],
  );
  const fullExpr: Term[] = useMemo(
    () => (current ? [...committed, current] : committed),
    [committed, current],
  );
  const total = useMemo(() => sumSigned(fullExpr), [fullExpr]);

  // ----------------------- Submit (no buffer clear) --------------------

  // `dirty` tracks whether the expression holds an edit not yet sent to the
  // server, so the unmount flush doesn't fire a redundant PATCH.
  const dirtyRef = useRef(false);

  const submit = useCallback(() => {
    // Nothing entered yet → don't auto-submit (would clobber to 0). But a
    // lone "0" IS an entry: "counted as zero" (stock legitimately depleted)
    // commits an empty expression at counted_qty 0 — distinct from the
    // untouched/uncounted state. (fullExpr is [] + total 0 in that case.)
    if (digits === "" && committed.length === 0) return;
    dirtyRef.current = false;
    onCommit(fullExpr, total);
  }, [digits, committed, fullExpr, total, onCommit]);

  // Debounced server submit. Re-arms on every change; the buffer is
  // deliberately NOT cleared after firing, so the operator keeps editing
  // the same value.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    if (digits === "" && committed.length === 0) {
      timerRef.current = null;
      return;
    }
    timerRef.current = setTimeout(() => submit(), AUTOCOMMIT_MS);
    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
    // Re-arm on the inputs that define the expression. submit is recreated
    // each render with the latest values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [digits, mode, committed]);

  // Flush a pending edit on unmount (operator taps another row before the
  // debounce fires). Latest-ref pattern: the unmount-only effect calls the
  // freshest `submit`, which still carries THIS row's onCommit closure.
  const flushRef = useRef(submit);
  useEffect(() => {
    flushRef.current = submit;
  });
  useEffect(
    () => () => {
      if (dirtyRef.current) flushRef.current();
    },
    [],
  );

  // Report the live running total up so the info bar's Counted/Mismatch
  // reflect what's being typed, not just the last server submit — this is
  // what stops the centered counted value from jumping between the
  // committed and the live value on each debounce.
  useEffect(() => {
    // Report 0 for an explicit "counted as zero" entry too (a lone "0"),
    // not just non-empty expressions, so the info bar shows 0 not "—".
    const entered = digits !== "" || committed.length > 0;
    onLiveTotal?.(entered ? total : null);
  }, [total, digits, committed, onLiveTotal]);

  // ----------------------- Handlers ------------------------------------

  const onDigit = (d: number) => {
    if (disabled) return;
    dirtyRef.current = true;
    setDigits((prev) => {
      // A lone "0" is a valid entry — "counted as zero" (stock depleted).
      // A leading zero before another digit is replaced so "0" then "5"
      // becomes "5", not "05".
      if (prev === "0") return d === 0 ? "0" : String(d);
      return prev + String(d);
    });
  };
  const onBackspace = () => {
    if (disabled) return;
    dirtyRef.current = true;
    setDigits((prev) => prev.slice(0, -1));
  };
  const onModeTap = (k: TermKind) => {
    if (disabled) return;
    if (k === "crates" && (packSize == null || packSize <= 0)) return;
    // Seal the current entry (if any) and start a NEW addition/subtraction
    // of kind k. An empty buffer just selects the kind for the next digits.
    dirtyRef.current = true;
    if (current !== null) setCommitted((prev) => [...prev, current]);
    setMode(k);
    setDigits("");
  };
  const onClear = () => {
    if (disabled) return;
    // Clear only the CURRENT count type (the active kind): drop its
    // committed terms + the in-flight entry, keep the other kinds, and
    // STAY in this mode so the operator can immediately re-enter it.
    const kept = committed.filter((t) => t.kind !== mode);
    dirtyRef.current = true;
    setCommitted(kept);
    setDigits("");
    onCommit(kept, sumSigned(kept));
  };
  const onUndo = () => {
    if (disabled) return;
    // Step back one term: re-open the most recent SEALED term in the
    // buffer so the operator can edit its value (discarding the current
    // in-flight entry). With nothing sealed, just clear the in-flight one.
    if (committed.length === 0) {
      if (digits !== "") {
        dirtyRef.current = true;
        setDigits("");
      }
      return;
    }
    const last = committed[committed.length - 1];
    dirtyRef.current = true;
    setCommitted((prev) => prev.slice(0, -1));
    setDigits(String(last.n));
    setMode(last.kind);
  };

  // ----------------------- Render --------------------------------------

  const previewN = digits === "" ? 0 : Number.parseInt(digits, 10);
  const committedStr = render(committed);
  // The in-flight entry, formatted inline for the calc line: leading
  // operator (none on the first term), kind icon, then the value. Crates
  // show `n×pack`; the kind is also signalled by the lit mode button.
  const modeIcon = mode === "crates" ? "📦" : mode === "broken" ? "🚫" : "🔢";
  const op = committed.length === 0 ? "" : mode === "broken" ? "− " : "+ ";
  const valueText =
    mode === "crates" && packSize ? `${previewN}×${packSize}` : `${previewN}`;
  const currentText = `${op}${modeIcon} ${valueText}`;
  const hasCurrent = digits !== "";
  const hasAnything = hasCurrent || committed.length > 0;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, minmax(56px, 1fr))",
        gap: 6,
        padding: 8,
      }}
    >
      {/* Single CALC LINE — sealed expression (normal) + the in-flight
          entry (bold, larger) + running total. Replaces the old separate
          preview row to save vertical space; the current mode is also
          shown by the highlighted mode button. */}
      <div
        style={{
          gridColumn: "1 / -1",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: 8,
          minHeight: 44,
          padding: "6px 10px",
          borderRadius: 8,
          background: "var(--ant-color-fill-quaternary, rgba(0,0,0,0.04))",
          fontFamily: "var(--font-mono, monospace)",
        }}
      >
        <span style={{ fontSize: 15, lineHeight: 1.3, overflowWrap: "anywhere" }}>
          {committedStr && <span style={{ opacity: 0.85 }}>{committedStr}</span>}
          {hasCurrent ? (
            <span style={{ fontWeight: 700, fontSize: 22 }}>
              {committedStr ? " " : ""}
              {currentText}
            </span>
          ) : (
            // Empty buffer: still show the active kind's icon (+ operator)
            // so tapping a mode reflects in the calc line before any digit.
            <span style={{ opacity: 0.5, fontWeight: 600, fontSize: 18 }}>
              {committedStr ? " " : ""}
              {op}
              {modeIcon} …
            </span>
          )}
        </span>
        {hasAnything && (
          <span style={{ fontWeight: 700, fontSize: 18, whiteSpace: "nowrap" }}>
            = {total}
          </span>
        )}
      </div>

      {/* Digits 1-9 with the mode selectors stacked in the right column. */}
      {([7, 8, 9] as const).map((d) => (
        <KeypadButton key={d} label={String(d)} onPress={() => onDigit(d)} disabled={disabled} />
      ))}
      <KeypadButton
        // Stays in place + greyed on loose-only articles — never reflow
        // the grid, so the operator's muscle memory for button positions
        // holds across articles.
        label="📦 crates"
        onPress={() => onModeTap("crates")}
        disabled={disabled || packSize == null || packSize <= 0}
        accent={mode === "crates"}
        compact
      />
      {([4, 5, 6] as const).map((d) => (
        <KeypadButton key={d} label={String(d)} onPress={() => onDigit(d)} disabled={disabled} />
      ))}
      <KeypadButton
        label="🚫 broken"
        onPress={() => onModeTap("broken")}
        disabled={disabled}
        accent={mode === "broken"}
        compact
      />
      {([1, 2, 3] as const).map((d) => (
        <KeypadButton key={d} label={String(d)} onPress={() => onDigit(d)} disabled={disabled} />
      ))}
      <KeypadButton
        label="🔢 loose"
        onPress={() => onModeTap("loose")}
        disabled={disabled}
        accent={mode === "loose"}
        compact
      />
      <KeypadButton label="C" onPress={onClear} disabled={disabled} danger />
      <KeypadButton label="0" onPress={() => onDigit(0)} disabled={disabled} />
      <KeypadButton label="⌫" onPress={onBackspace} disabled={disabled} />
      <KeypadButton
        label="↶ undo"
        onPress={onUndo}
        disabled={disabled || (digits === "" && committed.length === 0)}
        compact
      />
    </div>
  );
};

interface KeyProps {
  label: string;
  onPress: () => void;
  disabled?: boolean;
  accent?: boolean;
  danger?: boolean;
  /** Verb buttons (mode selectors, undo) use a smaller wrapping label so
   * "📦 crates" fits a ~56px column at phone width instead of clipping. */
  compact?: boolean;
}

const KeypadButton = ({
  label,
  onPress,
  disabled,
  accent,
  danger,
  compact,
}: KeyProps) => (
  <Button
    type={accent ? "primary" : "default"}
    danger={danger}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onPress}
    disabled={disabled}
    style={{
      minHeight: 56,
      minWidth: 44,
      fontSize: compact ? 13 : 18,
      fontWeight: 500,
      lineHeight: 1.15,
      whiteSpace: "normal",
      padding: compact ? "2px 4px" : undefined,
      touchAction: "manipulation",
    }}
  >
    {label}
  </Button>
);
